import { useMutation } from '@tanstack/react-query';
import { useAddSurveyToSurveyBank } from '@/features/survey_bank/hooks/useMutation/useAddSurveyToSurveyBank';
import { useAddQuestionsToSurveyBankQuestionBank } from '@/lib/useQuery/surveyBankQuestionBank/useAddQuestionsToSurveyBankQuestionBank';
import { Tables } from '@/lib/supabase/supabase_types_override';
import { QuestionConditionals } from '@/types/crm/configuration/configuration';
import { Where } from '@/types/crm/configuration/question_conditionals';
import { DuplicateSurveyTemplateParams, SurveyTemplateData } from './useDuplicateSurveyTemplate.types';

function replaceOldIdsWithNewIds({
    conditionals, 
    questions
}: {
    conditionals?: QuestionConditionals;
    questions: { id: Tables<'survey_bank_question_bank'>['id']; newId: string; }[] | [];
}) {
    if(!conditionals || !Array.isArray(questions) || questions.length === 0) {
        return conditionals;
    }
    
    const oldIdToNewId = Object.fromEntries(questions.map(q => [q.id, q.newId]));

    function transformWhere(where: Where): Where {
        const result: Where = {};
        
        for (const [key, value] of Object.entries(where)) {
            if (key === 'and' || key === 'or') {
                if (Array.isArray(value)) {
                    result[key] = value.map(transformWhere);
                }
            } else {
                const newKey = oldIdToNewId[key] || key;
                if (Array.isArray(value)) {
                    result[newKey] = value.map(transformWhere);
                } else {
                    result[newKey] = value;
                }
            }
        }
        
        return result;
    }

    return {
        ...conditionals,
        where: conditionals.where ? transformWhere(conditionals.where) : undefined
    };
}

function prepareQuestionsWithNewIds({ questions }: { questions?: Tables<'survey_bank_question_bank'>[] }) {
    if (!questions) {
        throw new Error('Questions are required');
    }

    return questions
        ?.map(question => {
            return {
                ...question,
                newId: self.crypto.randomUUID(),
            }
        })
        ?.map(({newId, ...question}, _index, questionsArr) => {
            return { 
                ...question,
                id: newId,
                selected_configuration: {
                    ...question.selected_configuration,
                    conditionals: replaceOldIdsWithNewIds({conditionals: question.selected_configuration?.conditionals, questions: questionsArr}),
                },
            }
        }) || [];
}

export function useDuplicateSurveyTemplate() {
    const { mutateAsync: addSurveyToSurveyBank } = useAddSurveyToSurveyBank();
    const { mutateAsync: addQuestionsToSurveyBankQuestionBank } = useAddQuestionsToSurveyBankQuestionBank();

    return useMutation({
        mutationFn: async ({
            surveyTemplate,
            params
        }: {
            surveyTemplate: SurveyTemplateData;
            params: DuplicateSurveyTemplateParams;
        }) => {
            const { survey_bank_question_bank } = surveyTemplate;

            const surveyRequestBody = {
                name: params.name,
                description: params.description,
                archived: false,
                crm_id: surveyTemplate.crm_id,
                crm_config_id: surveyTemplate.crm_config_id,
                crm_system: surveyTemplate.crm_system,
                organisation_id: surveyTemplate.organisation_id,
                survey_ref_id: surveyTemplate.survey_ref_id,
            };

            const surveyBankResponse = await addSurveyToSurveyBank(surveyRequestBody);
            const surveyId = surveyBankResponse?.id;
            
            if (!surveyId) {
                throw new Error('Failed to create survey template');
            }

            const questionsWithNewIds = prepareQuestionsWithNewIds({ questions: survey_bank_question_bank });
            const questionBankRequestBody = questionsWithNewIds?.map(({ ...question }) => ({
                ...question,
                survey_bank_id: surveyId,
            })) || [];

            await addQuestionsToSurveyBankQuestionBank(questionBankRequestBody);

            return { surveyId };
        },
    });
}